import { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useColorMode } from '@chakra-ui/react';
import { Button } from '@peacecover/component';
import { t } from 'i18next';
import { ArrowLeft } from '@peacecover/icon';
import Bubble from '../../components/floating/Bubble';
import RefundExchangeNotice from '../../chat/components/RefundExchangeNotice';
import { BettieGradientColor } from '../../../constants/BettieGradientColor';
import type { components } from '../../../types/schema';

const SELLING_ITEMS = [
  {
    id: 1,
    name: '100회 묶음 단건 충전',
    price: 3000,
    pricePerCount: 30
  },
  {
    id: 2,
    name: '50회 묶음 단건 충전',
    price: 1500,
    pricePerCount: 30
  }
];

interface SellingItem {
  id: number | string;
  name: string;
  price: number;
  pricePerCount: number;
}

const formatPrice = (price: number) => price.toLocaleString('ko-KR');

const SellingItemRow = ({
  item,
  highlight = false
}: {
  item: SellingItem;
  highlight?: boolean;
}) => {
  const { colorMode } = useColorMode();
  if (highlight) {
    return (
      <div className="relative">
        <Bubble
          text={t('BettieSellingListPopup.currentSubscription')}
          gradientDirection="center"
          className="absolute top-[11px] left-[16px]"
        />
        <div
          className="rounded-[17px] p-[1px]"
          style={BettieGradientColor.bettieProductOutline[colorMode]}
        >
          <div
            className="flex items-center justify-between px-[17px] py-[23px] rounded-[16px]"
            style={BettieGradientColor.bettieProductFill[colorMode]}
          >
            <p className="text-[15px] font-semibold text-text-main-lv2 leading-[18px]">
              {item.name}
            </p>
            <div className="flex items-center gap-[6px]">
              <p className="text-[15px] font-semibold text-text-main-lv2 leading-[18px]">
                {formatPrice(item.price)}원
              </p>
              <p className="text-[13px] font-medium text-text-main-lv4 leading-[16px]">
                1개당 {formatPrice(item.pricePerCount)}원
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between px-[18px] py-[24px] rounded-[16px] bg-background-elevated border border-separator2">
      <p className="text-[15px] font-semibold text-text-main-lv2 leading-[18px]">{item.name}</p>
      <div className="flex items-center gap-[6px]">
        <p className="text-[15px] font-semibold text-text-main-lv2 leading-[18px]">
          {formatPrice(item.price)}원
        </p>
        <p className="text-[13px] font-medium text-text-main-lv4 leading-[16px]">
          1개당 {formatPrice(item.pricePerCount)}원
        </p>
      </div>
    </div>
  );
};

type MySubScriptionType = components['schemas']['ApiV1Subscription'];

interface BettieSellingItemListPopupProps {
  onClose: () => void;
  mySubscription?: MySubScriptionType;
}

const BettieSellingListPopup = ({ onClose, mySubscription }: BettieSellingItemListPopupProps) => {
  const navigate = useNavigate();
  const sellingItems = useMemo(() => {
    if (!mySubscription) {
      return SELLING_ITEMS;
    }

    const subscriptionItem: SellingItem = {
      id: mySubscription.subscriptionId,
      name: mySubscription.productName,
      price: Number(mySubscription.amount ?? SELLING_ITEMS[0].price),
      pricePerCount: mySubscription.productType === 'standard' ? 19 : 8
    };

    return [subscriptionItem, ...SELLING_ITEMS];
  }, [mySubscription]);

  return createPortal(
    <div className="fixed inset-0 z-[1000] bg-background-light dark:bg-background-dark">
      <div className="min-h-screen pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
        <div className="sticky top-0 grid grid-cols-3 items-center px-[16px] py-[10px] bg-background-light dark:bg-background-dark">
          <ArrowLeft className="text-text-main-lv1 justify-self-start" onClick={onClose} />
        </div>
        <div className="flex flex-col gap-[14px] px-[24px] py-[10px] mt-[14px]">
          <h1 className="text-[26px] font-bold text-text-main-lv1 leading-[31px] whitespace-pre-line">
            {t('BettieSellingListPopup.title')}
          </h1>
          <p className="text-[15px] font-medium text-text-main-lv4 leading-[18px]">
            {t('BettieSellingListPopup.description')}
          </p>
        </div>
        <div className="px-[24px] flex flex-col gap-[10px] mt-[20px]">
          {sellingItems.map((item, index) => (
            <SellingItemRow key={item.id} item={item} highlight={index === 0} />
          ))}
        </div>
        <div className="mt-[169px] flex flex-col gap-[48px] items-center">
          <Button
            size="medium"
            btnColor="gray"
            className="text-[14px] font-semibold leading-[17px] px-[18px]"
            onClick={() => navigate(`/subscription/${mySubscription?.subscriptionId}/cancel`)}
          >
            {t('BettieSellingListPopup.cancelButton')}
          </Button>
          <RefundExchangeNotice />
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BettieSellingListPopup;
